import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DictationActivityEvent } from '../shared/models';
import { PipelineActivityLogService } from '../shared/services/pipeline-activity-log.service';
import { AudioDictationService } from '../shared/services/audio-dictation.service';
import { MedicalNecessityPdfService } from '../shared/services/medical-necessity-pdf.service';
import { ClinicalDocumentsPdfService } from '../shared/services/clinical-documents-pdf.service';
import { formatPatientName, getAvatarColor, getInitials } from '../shared/utils/avatar.util';
import {
  getMedicalNecessityIndicator,
  getMedicalNecessityIndicatorLabel,
  MedicalNecessityIndicator,
} from '../shared/utils/medical-necessity.util';
import { DocumentViewerModalComponent } from '../shared/components/document-viewer-modal/document-viewer-modal.component';

interface BillingCodeRow {
  code: string;
  reason: string;
}

interface OrderBillingGroup {
  orderId: string;
  description: string;
  cptCodes: BillingCodeRow[];
  icdCodes: BillingCodeRow[];
}

// Mirrors the rows saveBillingSummary() persists into billing_cpt_codes / billing_icd_codes
// (app/helpers/billingHelper.js) — sourced client-side from the same billing_analysis JSON.
interface EncounterCptCode {
  code: string;
  description: string;
  reason: string;
  isAddOn: boolean;
}

interface EncounterIcdCode {
  code: string;
  cptCode: string;
  reason: string;
}

// One row per available document for the Files panel — a plain list with
// View/Download actions, no inline content. Closures target whichever entity
// (dictation vs. action item) the underlying view/download method expects.
interface FileRow {
  key: string;
  icon: string;
  label: string;
  subtitle?: string;
  itemId?: string;
  view: () => void;
  download: () => void;
}

interface TodayDictation {
  id: string;
  provider?: { description: string };
  created_at: string;
  transcription?: string;
  doctorNotesParsed?: any;
  billingParsed?: any;
  billingGroups: OrderBillingGroup[];
  encounterCptCodes: EncounterCptCode[];
  encounterIcdCodes: EncounterIcdCode[];
  action_items: any[];
  fileRows: FileRow[];
}

const CATEGORY_ICONS: { [key: string]: string } = {
  insurance_harvest: 'fa-id-card',
  ort_master_harvest: 'fa-notes-medical',
  dictation: 'fa-microphone-alt',
  action_item: 'fa-tasks',
  order: 'fa-file-medical',
  medical_necessity: 'fa-shield-alt',
  appointment_processing: 'fa-calendar-times',
};

@Component({
  selector: 'app-live-operations',
  templateUrl: './live-operations.component.html',
  styleUrls: ['./live-operations.component.scss'],
})
export class LiveOperationsComponent implements OnInit, OnDestroy {
  // Live logs (top-left) — always live, in both modes
  logs: DictationActivityEvent[] = [];
  logsLoading = true;
  logsLoadingMore = false;
  private logsNextPage: number | null = null;
  private readonly logsPageSize = 40;
  private logsInterval: any;

  // Log source filter — every log's `source` is either the NextGen/RadMD bot (EHR card
  // scanning, ORT master harvest) or "backend" (the audio dictation pipeline: dictation,
  // action items, orders, medical necessity all fire from there), so this maps directly
  // onto that field rather than introducing a separate category-based grouping.
  sourceFilter: 'all' | 'ehr' | 'audio' = 'all';

  private get sourceFilterParam(): string | undefined {
    switch (this.sourceFilter) {
      case 'ehr':
        return 'nextgen_bot,radmd_bot';
      case 'audio':
        return 'backend';
      default:
        return undefined;
    }
  }

  // Dashboard mode: 'live' auto-follows the most recent log's patient;
  // 'static' freezes on whatever patient was last clicked in the log feed.
  mode: 'live' | 'static' = 'live';
  selectedLogId: string | null = null;

  // Current patient tracking (auto-followed in live mode, manually pinned in static mode)
  currentPatientId: string | null = null;
  currentPatientName = '';
  currentPatientMrn = '';
  currentPatientInsurance = '';

  // Today's dictations for the current patient (feeds top-right, bottom-left, bottom-right)
  dictations: TodayDictation[] = [];
  dictationsLoading = false;
  private dictationsInterval: any;
  // Guards against an older poll resolving after a newer one (e.g. under network jitter) and
  // clobbering fresher data with stale data — only the response matching the latest request wins.
  private dictationsRequestSeq = 0;

  lastUpdated: Date = new Date();

  // Tracks which action item currently has a PDF generation in flight (disables its button)
  generatingPdfForItemId: string | null = null;

  // Provider filter
  providers: { provider_id: string; description: string }[] = [];
  selectedProviderId: string | null = null;
  private practiceId: string | null = null;
  private readonly ALL_PROVIDERS_EMAILS = [
    'hamza@vytlsft.com',
    'hamza@verahealth.com',
    'jeremy@vytlsft.com',
    'jealand@spoc-ortho.com'
  ];

  // Flattened action items across today's dictations, for the Action Items panel.
  // Computed once whenever `dictations` changes (not a getter) — a getter that returns
  // new object references on every read causes Angular to destroy/recreate this list's
  // DOM on every change-detection cycle (e.g. on hover), which breaks click handling.
  allActionItems: any[] = [];

  constructor(
    private pipelineActivityLogService: PipelineActivityLogService,
    private audioDictationService: AudioDictationService,
    private medicalNecessityPdfService: MedicalNecessityPdfService,
    private clinicalDocumentsPdfService: ClinicalDocumentsPdfService,
    private modalService: NgbModal,
    private spinnerService: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.practiceId = localStorage.getItem('PRACTICE_ID');
    this.loadProviders();
    this.fetchLogs();
    this.logsInterval = setInterval(() => this.fetchLogs(), 5 * 1000);
    this.dictationsInterval = setInterval(() => this.fetchTodaysDictations(), 8 * 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.logsInterval);
    clearInterval(this.dictationsInterval);
  }

  setSourceFilter(filter: 'all' | 'ehr' | 'audio'): void {
    if (this.sourceFilter === filter) return;
    this.sourceFilter = filter;
    this.logs = [];
    this.logsNextPage = null;
    this.logsLoading = true;
    this.fetchLogs();
  }

  private async loadProviders(): Promise<void> {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const email = user?.email?.toLowerCase() ?? '';
      const seeAll = this.ALL_PROVIDERS_EMAILS.includes(email);
      console.log(seeAll)
      const res = await this.audioDictationService.getAiProviders(seeAll ? undefined : this.practiceId ?? undefined);
      this.providers = res?.data || [];
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  }

  selectProvider(providerId: string | null): void {
    this.selectedProviderId = providerId || null;
    this.logs = [];
    this.logsNextPage = null;
    this.logsLoading = true;
    this.fetchLogs();
    if (this.currentPatientId) this.fetchTodaysDictations();
  }

  private fetchLogs(): void {
    this.pipelineActivityLogService
      .getActivityLogs({ page: 1, limit: this.logsPageSize, source: this.sourceFilterParam, provider_id: this.selectedProviderId ?? undefined })
      .then((res) => {
        this.logsLoading = false;
        const incoming = res?.data?.data ?? [];
        if (!this.logs.length) {
          this.logs = incoming;
          this.logsNextPage = res?.data?.nextPage ?? null;
        } else {
          // Merge fresh logs at the top instead of replacing the list, so pages
          // loaded via infinite scroll aren't wiped out on every poll.
          const existingIds = new Set(this.logs.map((l) => l.id));
          const fresh = incoming.filter((l) => !existingIds.has(l.id));
          if (fresh.length) this.logs = [...fresh, ...this.logs];
        }
        this.lastUpdated = new Date();
        this.detectCurrentPatient();
      })
      .catch((err) => {
        this.logsLoading = false;
        console.error('Failed to load live logs:', err);
      });
  }

  onLogsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
      this.loadMoreLogs();
    }
  }

  private loadMoreLogs(): void {
    if (this.logsLoadingMore || !this.logsNextPage) return;
    this.logsLoadingMore = true;
    this.pipelineActivityLogService
      .getActivityLogs({ page: this.logsNextPage, limit: this.logsPageSize, source: this.sourceFilterParam, provider_id: this.selectedProviderId ?? undefined })
      .then((res) => {
        this.logsLoadingMore = false;
        const incoming = res?.data?.data ?? [];
        // New logs arriving at the top shift page offsets, so dedupe by id.
        const existingIds = new Set(this.logs.map((l) => l.id));
        const older = incoming.filter((l) => !existingIds.has(l.id));
        if (older.length) this.logs = [...this.logs, ...older];
        this.logsNextPage = res?.data?.nextPage ?? null;
      })
      .catch((err) => {
        this.logsLoadingMore = false;
        console.error('Failed to load more logs:', err);
      });
  }

  trackByLogId(_index: number, log: DictationActivityEvent): string {
    return log.id;
  }

  private detectCurrentPatient(): void {
    if (this.mode !== 'live') return;

    const latestWithPatient = this.logs.find((l) => l.patient_id && l.patient);
    if (!latestWithPatient || !latestWithPatient.patient) return;

    this.focusPatient(latestWithPatient);
  }

  /** Sets the focus patient (used by both live auto-follow and static-mode clicks). */
  private focusPatient(log: DictationActivityEvent): void {
    if (!log.patient || !log.patient_id) return;
    if (log.patient_id === this.currentPatientId) return;

    this.currentPatientId = log.patient_id;
    this.currentPatientName = formatPatientName(log.patient.first_name, log.patient.last_name);
    this.currentPatientMrn = log.patient.med_rec_nbr || '';
    this.currentPatientInsurance = '';
    this.fetchTodaysDictations();
  }

  setMode(mode: 'live' | 'static'): void {
    if (this.mode === mode) return;
    this.mode = mode;
    if (mode === 'live') {
      this.selectedLogId = null;
      this.detectCurrentPatient();
    }
  }

  selectLog(log: DictationActivityEvent): void {
    if (this.mode !== 'static' || !log.patient) return;
    this.selectedLogId = log.id;
    this.focusPatient(log);
  }

  private fetchTodaysDictations(): void {
    if (!this.currentPatientId) return;
    const requestId = ++this.dictationsRequestSeq;
    this.dictationsLoading = true;
    this.audioDictationService
      .getTodaysDictationsByPatient(this.currentPatientId, this.selectedProviderId ?? undefined)
      .then((res) => {
        if (requestId !== this.dictationsRequestSeq) return; // a newer request has since started — drop this stale response
        this.dictationsLoading = false;
        this.dictations = (res?.data ?? []).map((d: any) => this.mapDictation(d));
        this.allActionItems = this.dictations.flatMap((d) =>
          (d.action_items || []).map((item) => ({ ...item, dictationTime: d.created_at }))
        );
        const insurance = (res?.data ?? []).find((d: any) => d.insurance)?.insurance;
        if (insurance) this.currentPatientInsurance = insurance;
      })
      .catch((err) => {
        if (requestId !== this.dictationsRequestSeq) return;
        this.dictationsLoading = false;
        console.error('Failed to load today\'s dictations:', err);
      });
  }

  private mapDictation(raw: any): TodayDictation {
    const transcriptionRecord = raw.ai_transcriptions?.[0];
    let doctorNotesParsed: any = null;
    let billingParsed: any = null;

    if (transcriptionRecord?.doctor_notes) {
      try {
        doctorNotesParsed = JSON.parse(transcriptionRecord.doctor_notes);
      } catch {
        doctorNotesParsed = null;
      }
    }
    if (transcriptionRecord?.billing_analysis) {
      try {
        billingParsed = JSON.parse(transcriptionRecord.billing_analysis);
      } catch {
        billingParsed = null;
      }
    }

    // Same source data (and same regular+add_on / icd10_codes|icd_codes shape) that
    // saveBillingSummary() persists into billing_cpt_codes / billing_icd_codes.
    const allCptEntries = [
      ...(billingParsed?.cpt_codes || []).map((c: any) => ({ ...c, isAddOn: false })),
      ...(billingParsed?.add_on_codes || []).map((c: any) => ({ ...c, isAddOn: true })),
    ];
    const encounterCptCodes: EncounterCptCode[] = allCptEntries
      .map((c: any) => ({
        code: c.cpt_code,
        description: c.description || '',
        reason: c.reason || '',
        isAddOn: c.isAddOn,
      }))
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    const encounterIcdCodes: EncounterIcdCode[] = [];
    allCptEntries.forEach((c: any) => {
      (c.icd10_codes || c.icd_codes || []).forEach((icd: any) => {
        const code = typeof icd === 'object' ? (icd.code || '') : String(icd);
        const reason = typeof icd === 'object' ? (icd.reason || '') : '';
        if (code) encounterIcdCodes.push({ code, cptCode: c.cpt_code, reason });
      });
    });
    encounterIcdCodes.sort((a, b) => a.code.localeCompare(b.code));

    const actionItems = raw.action_items || [];

    // One group per order that has billing codes so far — orders with no codes yet (check
    // still pending/failed) are omitted entirely rather than rendered with an empty table.
    const billingGroups: OrderBillingGroup[] = actionItems
      .filter((item: any) => item.order?.billing_codes?.length)
      .map((item: any) => {
        const billingCodes = item.order.billing_codes;
        return {
          orderId: item.order.order_id,
          description: item.action_item || 'Order',
          cptCodes: billingCodes
            .filter((c: any) => c.code_type === 'cpt')
            .map((c: any) => ({ code: c.code, reason: c.reason || '' }))
            .sort((a, b) => a.code.localeCompare(b.code)),
          icdCodes: billingCodes
            .filter((c: any) => c.code_type === 'icd10')
            .map((c: any) => ({ code: c.code, reason: c.reason || '' }))
            .sort((a, b) => a.code.localeCompare(b.code)),
        };
      });

    const dictation: TodayDictation = {
      id: raw.id,
      provider: raw.provider,
      created_at: raw.created_at,
      transcription: transcriptionRecord?.transcription || '',
      doctorNotesParsed,
      billingParsed,
      billingGroups,
      encounterCptCodes,
      encounterIcdCodes,
      action_items: actionItems,
      fileRows: [],
    };
    dictation.fileRows = this.buildFileRows(dictation);

    return dictation;
  }

  // File rows only ever list documents that actually exist — nothing pending/empty gets a row.
  private buildFileRows(d: TodayDictation): FileRow[] {
    const rows: FileRow[] = [];

    if (d.doctorNotesParsed) {
      rows.push({
        key: `notes-${d.id}`,
        icon: 'fa-notes-medical',
        label: 'Doctor Notes',
        view: () => this.viewDoctorNotes(d),
        download: () => this.downloadDoctorNotesPdf(d),
      });
    }

    if (d.transcription) {
      rows.push({
        key: `transcript-${d.id}`,
        icon: 'fa-microphone-alt',
        label: 'Transcription',
        view: () => this.viewTranscription(d),
        download: () => this.downloadTranscriptionPdf(d),
      });
    }

    if (d.billingParsed?.billing_report || d.encounterCptCodes.length) {
      rows.push({
        key: `summary-${d.id}`,
        icon: 'fa-file-invoice-dollar',
        label: 'Summary',
        view: () => this.viewBillingSummary(d),
        download: () => this.downloadBillingSummaryPdf(d),
      });
    }

    d.action_items.forEach((item: any) => {
      if (!item.order?.lcd_coverage_details) return;
      const itemWithTime = { ...item, dictationTime: d.created_at };
      rows.push({
        key: `mn-${item.id}`,
        icon: 'fa-shield-alt',
        label: 'Medical Necessity',
        subtitle: item.action_item,
        itemId: item.id,
        view: () => this.viewMedicalNecessity(itemWithTime),
        download: () => this.downloadActionItemPdf(itemWithTime),
      });
    });

    return rows;
  }

  logPatientLabel(log: DictationActivityEvent): string {
    if (!log.patient) return '';
    return formatPatientName(log.patient.first_name, log.patient.last_name);
  }

  trackByActionItemId(_index: number, item: any): string {
    return item.id;
  }

  trackByDictationId(_index: number, dictation: TodayDictation): string {
    return dictation.id;
  }

  trackByOrderId(_index: number, group: OrderBillingGroup): string {
    return group.orderId;
  }

  trackByFileRowKey(_index: number, row: FileRow): string {
    return row.key;
  }

  actionItemStatus(item: any): 'accepted' | 'rejected' | 'pending' {
    if (item.order?.lcd_coverage_details) return 'accepted';
    if (item.rejected_ind) return 'rejected';
    return 'pending';
  }

  // Action Items panel order: billable items (those with an order) on top, non-billable
  // items (no order) at the bottom. Within billable items: Approved, then Not Approved,
  // then everything else (missing / not yet checked).
  private actionItemSortRank(item: any): number {
    if (!item?.order) return 3;
    switch (getMedicalNecessityIndicator(item.order)) {
      case 'approved':
        return 0;
      case 'not-approved':
        return 1;
      default:
        return 2;
    }
  }

  get sortedActionItems(): any[] {
    return [...this.allActionItems].sort((a, b) => this.actionItemSortRank(a) - this.actionItemSortRank(b));
  }

  medicalNecessityIndicator(item: any): MedicalNecessityIndicator {
    return getMedicalNecessityIndicator(item?.order);
  }

  medicalNecessityIndicatorLabel(item: any): string {
    return getMedicalNecessityIndicatorLabel(this.medicalNecessityIndicator(item));
  }

  downloadActionItemPdf(item: any): void {
    if (!this.currentPatientId || this.generatingPdfForItemId) return;
    this.generatingPdfForItemId = item.id;
    this.spinnerService.show();
    this.medicalNecessityPdfService
      .generate({
        item,
        patientId: this.currentPatientId,
        patientName: this.currentPatientName,
        patientMrn: this.currentPatientMrn,
        providerName: item.provider_name || '',
      })
      .then(() => {
        this.spinnerService.hide();
        this.generatingPdfForItemId = null;
      })
      .catch(() => {
        this.spinnerService.hide();
        this.generatingPdfForItemId = null;
        this.toastr.error('Failed to generate PDF report');
      });
  }

  viewDoctorNotes(d: TodayDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'doctor-notes';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.rawDoctorNotes = d.doctorNotesParsed;
  }

  downloadDoctorNotesPdf(d: TodayDictation): void {
    if (!d.doctorNotesParsed) return;
    this.clinicalDocumentsPdfService.generateDoctorNotesPdf(d.doctorNotesParsed, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewTranscription(d: TodayDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'transcription';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.transcriptionText = d.transcription || '';
  }

  downloadTranscriptionPdf(d: TodayDictation): void {
    if (!d.transcription) return;
    this.clinicalDocumentsPdfService.generateTranscriptionPdf(d.transcription, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewBillingSummary(d: TodayDictation): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'billing-summary';
    instance.timeLabel = this.formatDictationTime(d.created_at);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = d.provider?.description || '';
    instance.billingSummary = d.billingParsed;
  }

  downloadBillingSummaryPdf(d: TodayDictation): void {
    if (!d.billingParsed) return;
    this.clinicalDocumentsPdfService.generateBillingSummaryPdf(d.billingParsed, {
      patientName: this.currentPatientName,
      patientMrn: this.currentPatientMrn,
      providerName: d.provider?.description || '',
    });
  }

  viewMedicalNecessity(item: any): void {
    const modalRef = this.modalService.open(DocumentViewerModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'document-viewer-modal-window',
    });
    const instance: DocumentViewerModalComponent = modalRef.componentInstance;
    instance.docType = 'medical-necessity';
    instance.timeLabel = this.formatDictationTime(item.dictationTime);
    instance.patientName = this.currentPatientName;
    instance.patientMrn = this.currentPatientMrn;
    instance.providerName = item.provider_name || '';
    instance.actionItem = item;
    instance.download.subscribe(() => this.downloadActionItemPdf(item));
  }

  get initials(): string {
    return getInitials(this.currentPatientName);
  }

  get avatarColor(): string {
    return getAvatarColor(this.currentPatientId || this.currentPatientName || '');
  }

  // Opens the ORT Masters tab pre-seeded with whatever patient is currently in focus here —
  // that tab then keeps itself in sync by independently auto-following live activity, the
  // same way this page's 'live' mode does, so no cross-tab messaging is needed.
  openOrtMasters(): void {
    const params = new URLSearchParams();
    if (this.currentPatientId) params.set('patient_id', this.currentPatientId);
    if (this.currentPatientName) params.set('name', this.currentPatientName);
    if (this.currentPatientMrn) params.set('mrn', this.currentPatientMrn);
    if (this.selectedProviderId) params.set('provider_id', this.selectedProviderId);
    const query = params.toString();
    window.open(`/ort-masters${query ? '?' + query : ''}`, '_blank');
  }

  categoryIcon(category: string): string {
    return CATEGORY_ICONS[category] || 'fa-circle-info';
  }

  formatTime(value: string | Date): string {
    return moment(value).format('h:mm:ss.SSS A');
  }

  formatDictationTime(value: string): string {
    return moment(value).format('h:mm A');
  }
}
